
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { MessageHistory } from "./MessageHistory";
import { MessageSquare, Calendar, Briefcase, Heart, Target, Users } from "lucide-react";
import { format } from "date-fns";
import type { Database } from "@/integrations/supabase/types";

type Client = Database["public"]["Tables"]["clients"]["Row"];

interface ClientProfileProps {
  client: Client;
}

export function ClientProfile({ client }: ClientProfileProps) {
  const [showHistory, setShowHistory] = useState(false);
  const navigate = useNavigate();

  if (showHistory) {
    return <MessageHistory client={client} onBack={() => setShowHistory(false)} />;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Client Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="font-medium">Meeting Context</div>
                  <div className="text-sm text-muted-foreground">{client.meeting_context}</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="font-medium">Industry</div>
                  <div className="text-sm text-muted-foreground">{client.industry}</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="font-medium">Last Contact</div>
                  <div className="text-sm text-muted-foreground">
                    {format(new Date(client.last_contact_date), 'PPP')}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="font-medium">Service Information</div>
                  <div className="text-sm text-muted-foreground">
                    <div>Service Used: {client.service_used}</div>
                    <div>Outcome: {client.service_outcome}</div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="font-medium">Appreciation Note</div>
                  <div className="text-sm text-muted-foreground">{client.appreciation}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={() => navigate(`/client-info?id=${client.id}`)}
            >
              Edit Profile
            </Button>
            <Button onClick={() => setShowHistory(true)} className="gap-2">
              <MessageSquare className="h-4 w-4" />
              Message History
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
